import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchTests, generateQuestion, submitAnswer } from '../../utils/testApi';

function TestQuestion() {
  const navigate = useNavigate();
  const { themeId } = useParams();

  const [theme, setTheme] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [gradingResult, setGradingResult] = useState(null);
  const [loadingTheme, setLoadingTheme] = useState(true);
  const [loadingQuestion, setLoadingQuestion] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadTheme = useCallback(async () => {
    try {
      setLoadingTheme(true);
      const result = await fetchTests();
      const themes = result.data?.themes || [];
      const found = themes.find((item) => String(item.id) === String(themeId));
      if (!found) {
        setError('テーマが見つかりません');
        return;
      }
      setTheme(found);
    } catch (err) {
      setError(err.message || 'テーマ情報の取得に失敗しました');
    } finally {
      setLoadingTheme(false);
    }
  }, [themeId]);

  const loadQuestion = useCallback(async () => {
    try {
      setLoadingQuestion(true);
      setError('');
      setGradingResult(null);
      setAnswerText('');
      const result = await generateQuestion(themeId);
      setQuestion(result.data || null);
    } catch (err) {
      setError(err.message || '問題の生成に失敗しました');
      setQuestion(null);
    } finally {
      setLoadingQuestion(false);
    }
  }, [themeId]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  useEffect(() => {
    loadQuestion();
  }, [loadQuestion]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!question?.question_id) {
      setError('問題が読み込まれていません');
      return;
    }

    if (!answerText.trim()) {
      setError('回答を入力してください');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const result = await submitAnswer(themeId, {
        question_id: question.question_id,
        answer_text: answerText,
      });
      setGradingResult(result.data || null);
    } catch (err) {
      setError(err.message || '採点に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingTheme) {
    return (
      <div className="flex justify-center items-center min-h-[260px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{theme?.name || 'Webテスト'}</h1>
          <p className="text-sm text-gray-600 mt-1">1回の表示につき1問生成されます</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/tests')}
          className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
        >
          一覧へ戻る
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-5 md:p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">問題</h2>
          {loadingQuestion ? (
            <p className="text-sm text-gray-500 mt-2">問題を生成中です...</p>
          ) : question?.question ? (
            <p className="text-base text-gray-800 mt-2 whitespace-pre-wrap">{question.question}</p>
          ) : (
            <p className="text-sm text-gray-500 mt-2">問題を表示できませんでした</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">回答（記述式）</label>
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="回答を入力してください"
              disabled={loadingQuestion || submitting}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={loadingQuestion || submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? '採点中...' : '回答して採点'}
            </button>

            <button
              type="button"
              onClick={loadQuestion}
              disabled={loadingQuestion || submitting}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 disabled:opacity-60"
            >
              次の問題
            </button>
          </div>
        </form>

        {gradingResult && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-base font-semibold text-gray-900">採点結果</h3>
            <p className="text-sm text-gray-700 mt-2">score: {gradingResult.score}</p>
            <p className="text-sm text-gray-700 mt-1">
              判定: {gradingResult.correct ? 'correct' : 'incorrect'}
            </p>
            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{gradingResult.feedback}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TestQuestion;
